import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styles from '../../styles/LoginRegister.module.css';
import buttonStyles from '../../styles/Buttons.module.css';
import { useEnhancedAuth } from '../../contexts/EnhancedAuthContext';
import { SUBSCRIPTION_ENDPOINTS, STORAGE_KEYS } from '../../constants/api';

type PollStatus = 'polling' | 'success' | 'pending' | 'no-session';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 15; // ~30s — gives the PayFast ITN webhook time to arrive
const REDIRECT_COUNTDOWN_SECONDS = 3;

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const { checkPaymentStatus } = useEnhancedAuth();
  const [status, setStatus] = useState<PollStatus>('polling');
  const [countdown, setCountdown] = useState(REDIRECT_COUNTDOWN_SECONDS);
  const cancelledRef = useRef(false);

  const startPolling = useCallback(() => {
    const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) {
      setStatus('no-session');
      return;
    }

    setStatus('polling');
    cancelledRef.current = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(SUBSCRIPTION_ENDPOINTS.STATUS, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (cancelledRef.current) return;

        if (res.data?.paymentStatus === 'active') {
          setStatus('success');
          await checkPaymentStatus();
          return;
        }
      } catch {
        // transient errors just retry until attempts run out
      }

      if (cancelledRef.current) return;

      if (attempts >= MAX_POLL_ATTEMPTS) {
        setStatus('pending');
        return;
      }

      setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
  }, [checkPaymentStatus]);

  useEffect(() => {
    startPolling();
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-redirect to the dashboard once activation is confirmed
  useEffect(() => {
    if (status !== 'success') return;

    if (countdown <= 0) {
      navigate('/Dashboard');
      return;
    }

    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [status, countdown, navigate]);

  const getStatusIcon = () => {
    switch (status) {
      case 'polling':
        return <div className={styles.spinner} />;
      case 'success':
        return <div className={styles.successIcon}>✓</div>;
      default:
        return <div className={styles.errorIcon}>⏳</div>;
    }
  };

  const getStatusClass = () => {
    switch (status) {
      case 'success':
        return styles.verificationSuccess;
      case 'polling':
        return styles.verificationPending;
      default:
        return styles.verificationError;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.verifyEmailCard}>
        <div className={getStatusClass()}>
          {getStatusIcon()}
          <h2 className={styles.verifyTitle}>
            {status === 'polling' && 'Confirming Your Payment'}
            {status === 'success' && 'Payment Successful 🎉'}
            {status === 'pending' && 'Still Processing'}
            {status === 'no-session' && 'Session Expired'}
          </h2>
          <p className={styles.verifyMessage}>
            {status === 'polling' &&
              "We're confirming your payment with PayFast. This usually takes just a few seconds..."}
            {status === 'success' &&
              'Your subscription has been activated. Enjoy full access to RealtyGenie!'}
            {status === 'pending' &&
              "Your payment is taking longer than usual to confirm. This can happen if PayFast's notification is delayed — it doesn't mean anything went wrong."}
            {status === 'no-session' &&
              'We could not find an active session to check your payment status. Please log in to view your subscription.'}
          </p>

          {status === 'success' && (
            <div className={styles.redirectInfo}>
              <p>Redirecting to your dashboard in {countdown} seconds...</p>
              <button
                className={`${buttonStyles.button} ${buttonStyles.formPrimary}`}
                onClick={() => navigate('/Dashboard')}
              >
                Go to Dashboard Now
              </button>
            </div>
          )}

          {status === 'pending' && (
            <div className={styles.verifyActions}>
              <button
                className={`${buttonStyles.button} ${buttonStyles.formPrimary}`}
                onClick={startPolling}
              >
                Check Again
              </button>
              <button
                className={`${buttonStyles.button} ${buttonStyles.formSecondary}`}
                onClick={() => navigate('/ContactUs')}
              >
                Contact Support
              </button>
            </div>
          )}

          {status === 'no-session' && (
            <div className={styles.verifyActions}>
              <button
                className={`${buttonStyles.button} ${buttonStyles.formPrimary}`}
                onClick={() => navigate('/LoginRegister')}
              >
                Log In
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
